import { type CSSProperties, useCallback, useEffect, useState } from 'react';

interface GithubCredsResponse {
    skipOnboarding?: boolean;
    githubUserLogin?: string;
    hasCredentials?: boolean;
    appSlug?: string;
}

export default function ConfigureGithubPage() {
    const [creds, setCreds] = useState<GithubCredsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    const loadCreds = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await fetch('/api/github/creds');
            if (!resp.ok) {
                throw new Error(await resp.text());
            }
            const data: GithubCredsResponse = await resp.json();
            setCreds(data);
        } catch (err) {
            console.error('Failed to load GitHub credentials', err);
            setError('Failed to load GitHub configuration. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadCreds();
    }, [loadCreds]);

    const hasCredentials = Boolean(creds?.hasCredentials);
    const githubLogin = creds?.githubUserLogin ?? '';

    const handleConnect = () => {
        window.location.href = '/api/github/oauth/login';
    };

    const handleDisconnect = async () => {
        setActionLoading(true);
        setError(null);
        try {
            const resp = await fetch('/api/github/creds', { method: 'DELETE' });
            if (!resp.ok) {
                throw new Error(await resp.text());
            }
            await loadCreds();
        } catch (err) {
            console.error('Failed to disconnect GitHub', err);
            setError('Failed to disconnect GitHub. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleOpenSettings = () => {
        if (!creds?.appSlug) {
            setError('GitHub app settings are unavailable.');
            return;
        }
        const installURL = `https://github.com/apps/${creds.appSlug}/installations/select_target`;
        window.open(installURL, '_blank', 'noopener,noreferrer');
    };

    const wrapperStyle: CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 52px)',
        padding: '24px',
        gap: '16px',
        textAlign: 'center',
    };

    const buttonStyle: CSSProperties = {
        padding: '12px 24px',
        borderRadius: '9999px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 600,
        minWidth: '240px',
    };

    const dangerButtonStyle: CSSProperties = {
        ...buttonStyle,
        '--button-bg': '#c62828',
        '--button-bg-hover': '#b71c1c',
        '--button-bg-active': '#a11919',
        '--button-color': '#ffffff',
        '--button-color-hover': '#ffffff',
    } as CSSProperties;

    const secondaryButtonStyle: CSSProperties = {
        ...buttonStyle,
        '--button-bg': '#e8eaed',
        '--button-bg-hover': '#d2d6db',
        '--button-bg-active': '#c3c8ce',
        '--button-color': '#1f2933',
        '--button-color-hover': '#111827',
    } as CSSProperties;

    if (loading) {
        return (
            <div style={wrapperStyle}>
                <p>Loading GitHub configuration…</p>
            </div>
        );
    }

    return (
        <div style={wrapperStyle}>
            <h1 style={{ margin: 0 }}>Configure GitHub</h1>
            {hasCredentials ? (
                <p style={{ margin: 0 }}>
                    Connected as <strong>@{githubLogin || 'unknown'}</strong>
                </p>
            ) : (
                <p style={{ margin: 0 }}>
                    Connect your GitHub account to enable the agent to checkout code and create pull requests.
                </p>
            )}
            {error && (
                <div
                    style={{
                        backgroundColor: '#fdecea',
                        color: '#b71c1c',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        maxWidth: '420px',
                    }}
                >
                    <p style={{ margin: 0 }}>{error}</p>
                </div>
            )}
            {hasCredentials ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <button
                        type="button"
                        onClick={handleDisconnect}
                        disabled={actionLoading}
                        style={dangerButtonStyle}
                    >
                        {actionLoading ? 'Disconnecting…' : 'Disconnect'}
                    </button>
                    <button
                        type="button"
                        onClick={handleOpenSettings}
                        disabled={actionLoading}
                        style={secondaryButtonStyle}
                    >
                        Open GitHub Settings
                    </button>
                </div>
            ) : (
                <button type="button" onClick={handleConnect} style={buttonStyle}>
                    Connect to GitHub
                </button>
            )}
            {/* The standalone refresh button has been removed to streamline the UI. */}
        </div>
    );
}
